import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { datePartDateTrunc, getAllDatePartDateTrunc } from 'src/app/utils/sql';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { ICheckboxSettingDashboardItem } from '../common/items/setting/checkbox/checkbox.component';
import { IMultiSelectSettingDashboardItem } from '../common/items/setting/multi-select/multi-select.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { UsersdashActivityDailyDataLoaderFactory } from '../mopa-users/data-loaders/usersdash-activity-daily';
import { BiVizDashboardUsersActivity } from '../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { MetaEngagementByDayNoDataLoaderFactory } from './data-loaders/meta-engagement-by-day-no';
import { MetaEngagementDecosHistogramDataLoaderFactory } from './data-loaders/meta-engagement-decos-histogram';
import { MopaMetaEngagementChartDocs } from './mopa-meta-engagement.chart-documentation';

@Component({
    selector: 'app-mopa-meta-engagement',
    templateUrl: './mopa-meta-engagement.component.html',
    styleUrls: ['./mopa-meta-engagement.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaMetaEngagementComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'META Engagement';
    override DASHBOARD_ID = 'mopa-meta-engagement';
    values = Object.values;

    /** GCP Project id - resolved from the selected data project (URL `dataProject` param),
     * so MopaDev / MopaProd both work. Never hardcode. */
    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////    CHART DATALOADERS
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'mopa.meta-engagement.modify_time_users_datasource':
            DataLoaderFactories['common.modify_time']({
                createdInstanceId:
                    'mopa.meta-engagement.modify_time_users_datasource',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasetId: BiVizDashboardUsersActivity.sqlMap.dataset,
                tableId: BiVizDashboardUsersActivity.sqlMap.tableId,
            }).create(),
        'mopa.meta-engagement.dau': new UsersdashActivityDailyDataLoaderFactory(
            {
                createdInstanceId: 'mopa.meta-engagement.dau',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }
        ).create(),

        'mopa.meta-engagement.by_day_no':
            new MetaEngagementByDayNoDataLoaderFactory({
                createdInstanceId: 'mopa.meta-engagement.by_day_no',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'mopa.meta-engagement.decos_histogram':
            new MetaEngagementDecosHistogramDataLoaderFactory({
                createdInstanceId: 'mopa.meta-engagement.decos_histogram',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////    FILTER DATALOADERS
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'mopa.meta-engagement.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.meta-engagement.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
        'mopa.meta-engagement.filter.activity_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.meta-engagement.filter.app_version',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_APP_VERSION',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_APP_VERSION'),
        }).create(),
        'mopa.meta-engagement.filter.activity_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.meta-engagement.filter.activity_app_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.meta-engagement.filter.daily_total_energy_spent':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.meta-engagement.filter.daily_total_energy_spent',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'DAILY_TOTAL_ENERGY_SPENT',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                filterContext: true,
            }).create(),
        'mopa.meta-engagement.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.meta-engagement.filter.firebase_app_store',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_APP_STORE',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            })
        ).create(),
        'mopa.meta-engagement.filter.firebase_country_tier':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.meta-engagement.filter.firebase_country_tier',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            }).create(),
        'mopa.meta-engagement.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.meta-engagement.filter.firebase_country',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.meta-engagement.filter.firebase_country_us_region':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.meta-engagement.filter.firebase_country_us_region',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_US_REGION',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            }).create(),
        'mopa.meta-engagement.filter.firebase_country_european_union':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.meta-engagement.filter.firebase_country_european_union',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            }).create(),
        'mopa.meta-engagement.filter.attribution_traffic_source':
            DataLoaderFactories['common.filter.datasource_column'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.meta-engagement.filter.attribution_traffic_source',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                    datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                })
            ).create(),
        'mopa.meta-engagement.filter.firebase_install_version':
            DataLoaderFactories['common.filter.datasource_column'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.meta-engagement.filter.firebase_install_version',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
                    datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                    filterContext: true,
                    orderByQuery: datasourceColumnOrderByQuery(
                        'FIREBASE_INSTALL_VERSION'
                    ),
                })
            ).create(),
        'mopa.meta-engagement.filter.firebase_install_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.meta-engagement.filter.firebase_install_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.meta-engagement.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.meta-engagement.filter.etl_payer',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
        }).create(),
        'mopa.meta-engagement.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.meta-engagement.filter.etl_revenue_badge',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ETL_REVENUE_BADGE',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery('ETL_REVENUE_BADGE'),
            })
        ).create(),
        'mopa.meta-engagement.filter.attribution_network_name':
            DataLoaderFactories['common.filter.datasource_column'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.meta-engagement.filter.attribution_network_name',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
                    datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                })
            ).create(),
        'mopa.meta-engagement.filter.attribution_campaign_name':
            DataLoaderFactories['common.filter.datasource_column'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.meta-engagement.filter.attribution_campaign_name',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                    datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                    filterContext: true,
                })
            ).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////    SETTINGS
    /////////////////////////////////////////////////////////////////

    settingItemDateAggregation: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.DATE_AGGREGATION,
            viewtype: 'singleSelect',
            displayName: 'Date aggregation',
            value: 'day' as datePartDateTrunc,
            selectOptions: getAllDatePartDateTrunc(),
        },
    };

    settingSelectedDay: IMultiSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION,
            viewtype: 'multiSelect',
            displayName: 'Selected Day',
            value: [1],
            selectOptions: [
                0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
            ].map((d) => ({ value: d, label: `${d}` })),
        },
    };

    /** Day-mode toggle for the "by day_no" charts. Only install_day is offered for now;
     * active_day is disabled (see selectOptions) but the loader still supports it. */
    settingDayMode: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_DAY_MODE,
            viewtype: 'toggle',
            displayName: 'Day mode',
            description: 'Affects "by day_no" charts',
            value: 'install_day',
            // 'active_day' disabled for now - install-day only.
            selectOptions: ['install_day'],
        },
    };

    /** Limit max day_no shown in "by day_no" charts (same as Retention dashboard). */
    settingMaxDayNo: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.RETENTION_MAX_DAY_NO,
            viewtype: 'singleSelect',
            displayName: 'Limit max day no',
            value: this.getInitialContext().settings[
                QueryContextSettingID.RETENTION_MAX_DAY_NO
            ],
            selectOptions: ['60', '90', '180', '360', '720', 'unlimited'],
        },
    };

    settingShowAllUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_ALL_USERS,
            viewtype: 'checkbox',
            displayName: 'All',
            value: false,
        },
    };

    settingShowMetaUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_META_USERS,
            viewtype: 'checkbox',
            displayName: 'META',
            value: true,
        },
    };
    settingShowHopaUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_HOPA_USERS,
            viewtype: 'checkbox',
            displayName: 'HOPA',
            value: true,
        },
    };
    settingShowMetaProductiveUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_META_PRODUCTIVE_USERS,
            viewtype: 'checkbox',
            displayName: 'META Prod.',
            value: true,
        },
    };
    settingShowCoreLoopUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_CORE_LOOP_USERS,
            viewtype: 'checkbox',
            displayName: 'CORE LOOP',
            value: true,
        },
    };

    /////////////////////////////////////////////////////////////////
    ////    CHART DEFINITIONS
    /////////////////////////////////////////////////////////////////

    modify_time_users_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.modify_time_users_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.meta-engagement.modify_time_users_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.meta-engagement.modify_time_users_datasource'
            ].emitter,
    };

    // PROTOTYPE: DAU
    // Copy of the DAU chart in META Summary dashboard
    // No date aggregation
    dau: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.dau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.meta-engagement.chart.dau',
            dashboardItem: {
                title: 'DAU',
                subtitle: 'User type checkbox',
                chartDocumentation: MopaMetaEngagementChartDocs.dau,
            },
            xColumnLabel: 'date',
            echartsSeriesType: 'line',
            metricsGetter: (context) =>
                this.userTypeMetrics(context, {
                    all: 'all_dau',
                    meta: 'meta_dau',
                    hopa: 'hopa_dau',
                    coreLoop: 'core_loop_dau',
                    metaProd: 'meta_productive_dau',
                }),
            colorGetterBuilder: (context) => this.splitColorGetter(context),
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter: () =>
                    EChartsAxisHelper.axisLabelDateFormatter,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.dau'].emitter,
    };

    // PROTOTYPE: Activity Share by install day / active day
    // No date aggregation - x axis is day_no
    activity_share_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.activity_share_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.activity_share_by_day_no',
            dashboardItem: {
                title: (context: IQueryContext) =>
                    context.settings?.[QueryContextSettingID.META_DAY_MODE] ===
                    'install_day'
                        ? 'Activity Share per active user by install day'
                        : 'Activity Share by active day',
                subtitle: 'User type checkbox',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.activity_share_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            echartsSeriesType: 'line',
            metricsGetter: (context) =>
                this.userTypeMetrics(context, {
                    all: 'all_share',
                    meta: 'meta_share',
                    hopa: 'hopa_share',
                    coreLoop: 'core_loop_share',
                    metaProd: 'meta_productive_share',
                }),
            colorGetterBuilder: (context) => this.splitColorGetter(context),
            yAxisLabel: {
                formatter: (v: any) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    const dayLabel =
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Install day'
                            : 'Active day';
                    return (label: any) => `${dayLabel} ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
    };

    // PROTOTYPE: Median Lifetime Days Share by install day / active day
    median_lifetime_days_share_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.median_lifetime_days_share_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.median_lifetime_days_share_by_day_no',
            dashboardItem: {
                title: (context: IQueryContext) =>
                    context.settings?.[QueryContextSettingID.META_DAY_MODE] ===
                    'install_day'
                        ? 'Median Lifetime Days Share per active user by install day'
                        : 'Median Lifetime Days Share by active day',
                subtitle: 'User type checkbox',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.median_lifetime_days_share_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            echartsSeriesType: 'line',
            yAxisLabel: {
                formatter: (v: any) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            metricsGetter: (context) =>
                this.userTypeMetrics(context, {
                    all: 'median_all_lifetime_days_share',
                    meta: 'median_meta_lifetime_days_share',
                    hopa: 'median_hopa_lifetime_days_share',
                    coreLoop: 'median_core_loop_lifetime_days_share',
                    metaProd: 'median_meta_prod_lifetime_days_share',
                }),
            colorGetterBuilder: (context) => this.splitColorGetter(context),
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    const dayLabel =
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Install day'
                            : 'Active day';
                    return (label: any) => `${dayLabel} ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
                // secondaryMetricsGetter: () => [
                //     {
                //         name: 'Sample (n)',
                //         key: 'lifetime_days_share_n',
                //         formatter:
                //             EChartsTooltipHelper.tooltipIntegerValueFormatter,
                //     },
                // ],
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
    };

    // PROTOTYPE: Avg Lifetime Days Share by install day / active day
    // Mirror of median_lifetime_days_share_by_day_no using the avg_* columns - mean is outlier-
    // sensitive, so it reflects sparse-tail spikes the median hides.
    avg_lifetime_days_share_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.avg_lifetime_days_share_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.avg_lifetime_days_share_by_day_no',
            dashboardItem: {
                title: (context: IQueryContext) =>
                    context.settings?.[QueryContextSettingID.META_DAY_MODE] ===
                    'install_day'
                        ? 'Avg Lifetime Days Share per active user by install day'
                        : 'Avg Lifetime Days Share by active day',
                subtitle: 'User type checkbox',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.avg_lifetime_days_share_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            echartsSeriesType: 'line',
            yAxisLabel: {
                formatter: (v: any) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            metricsGetter: (context) =>
                this.userTypeMetrics(context, {
                    all: 'avg_all_lifetime_days_share',
                    meta: 'avg_meta_lifetime_days_share',
                    hopa: 'avg_hopa_lifetime_days_share',
                    coreLoop: 'avg_core_loop_lifetime_days_share',
                    metaProd: 'avg_meta_prod_lifetime_days_share',
                }),
            colorGetterBuilder: (context) => this.splitColorGetter(context),
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    const dayLabel =
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Install day'
                            : 'Active day';
                    return (label: any) => `${dayLabel} ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
                // secondaryMetricsGetter: () => [
                //     {
                //         name: 'Sample (n)',
                //         key: 'lifetime_days_share_n',
                //         formatter:
                //             EChartsTooltipHelper.tooltipIntegerValueFormatter,
                //     },
                // ],
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
    };

    // Median META Daily Time Share (%) per active user by install day / active day.
    // Single metric, independent of the user-type checkboxes; ctx denominator, raw/uncapped (may exceed 100%).
    median_meta_time_share_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.median_meta_time_share_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.median_meta_time_share_by_day_no',
            dashboardItem: {
                title: (context: IQueryContext) =>
                    context.settings?.[QueryContextSettingID.META_DAY_MODE] ===
                    'install_day'
                        ? 'Median META Daily Time Share (%) per active user by install day'
                        : 'Median META Daily Time Share (%) per active user by active day',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.median_meta_time_share_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            echartsSeriesType: 'line',
            yColumnLabel: 'median_meta_time_share',
            metricName: 'Median',
            colorGetterBuilder: (context) => this.splitColorGetter(context),
            yAxisLabel: {
                formatter: (v: any) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    const dayLabel =
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Install day'
                            : 'Active day';
                    return (label: any) => `${dayLabel} ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
                secondaryMetricsGetter: () => [
                    {
                        name: 'Sample (n)',
                        key: 'meta_time_share_n',
                        formatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    },
                ],
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
    };

    // Avg META Daily Time Share (%) per active user by install day / active day.
    avg_meta_time_share_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.avg_meta_time_share_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.avg_meta_time_share_by_day_no',
            dashboardItem: {
                title: (context: IQueryContext) =>
                    context.settings?.[QueryContextSettingID.META_DAY_MODE] ===
                    'install_day'
                        ? 'Avg META Daily Time Share (%) per active user by install day'
                        : 'Avg META Daily Time Share (%) per active user by active day',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.avg_meta_time_share_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            echartsSeriesType: 'line',
            yColumnLabel: 'avg_meta_time_share',
            metricName: 'Avg',
            colorGetterBuilder: (context) => this.splitColorGetter(context),
            yAxisLabel: {
                formatter: (v: any) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    const dayLabel =
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Install day'
                            : 'Active day';
                    return (label: any) => `${dayLabel} ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
                secondaryMetricsGetter: () => [
                    {
                        name: 'Sample (n)',
                        key: 'meta_time_share_n',
                        formatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    },
                ],
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
    };

    // UBI-1270: HOPA Daily Time Share as the residual of the client total-time counter (denominator
    // B) — the same denominator META and Shop use, so the three shares add up to 100%.
    median_hopa_time_share_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.median_hopa_time_share_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.median_hopa_time_share_by_day_no',
            dashboardItem: {
                title: (context: IQueryContext) =>
                    context.settings?.[QueryContextSettingID.META_DAY_MODE] ===
                    'install_day'
                        ? 'Median HOPA Daily Time Share (%) per active user by install day'
                        : 'Median HOPA Daily Time Share (%) per active user by active day',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.median_hopa_time_share_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            echartsSeriesType: 'line',
            yColumnLabel: 'median_hopa_time_share',
            metricName: 'Median',
            colorGetterBuilder: (context) => this.splitColorGetter(context),
            yAxisLabel: {
                formatter: (v: any) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    const dayLabel =
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Install day'
                            : 'Active day';
                    return (label: any) => `${dayLabel} ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
                secondaryMetricsGetter: () => [
                    {
                        name: 'Sample (n)',
                        key: 'hopa_time_share_n',
                        formatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    },
                ],
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
    };

    // Average counterpart of the HOPA Daily Time Share above.
    avg_hopa_time_share_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.avg_hopa_time_share_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.avg_hopa_time_share_by_day_no',
            dashboardItem: {
                title: (context: IQueryContext) =>
                    context.settings?.[QueryContextSettingID.META_DAY_MODE] ===
                    'install_day'
                        ? 'Avg HOPA Daily Time Share (%) per active user by install day'
                        : 'Avg HOPA Daily Time Share (%) per active user by active day',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.avg_hopa_time_share_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            echartsSeriesType: 'line',
            yColumnLabel: 'avg_hopa_time_share',
            metricName: 'Avg',
            colorGetterBuilder: (context) => this.splitColorGetter(context),
            yAxisLabel: {
                formatter: (v: any) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    const dayLabel =
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Install day'
                            : 'Active day';
                    return (label: any) => `${dayLabel} ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
                secondaryMetricsGetter: () => [
                    {
                        name: 'Sample (n)',
                        key: 'hopa_time_share_n',
                        formatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    },
                ],
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
    };

    // UBI-1270: HOPA Daily Time Share by session. Both inputs come from project_sessions.
    median_hopa_time_share_by_session_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.meta-engagement.chart.median_hopa_time_share_by_session_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.meta-engagement.chart.median_hopa_time_share_by_session_by_day_no',
                dashboardItem: {
                    title: (context: IQueryContext) =>
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Median HOPA Daily Time Share by session (%) per active user by install day'
                            : 'Median HOPA Daily Time Share by session (%) per active user by active day',
                    chartDocumentation:
                        MopaMetaEngagementChartDocs.median_hopa_time_share_by_session_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                echartsSeriesType: 'line',
                yColumnLabel: 'median_hopa_time_share_by_session',
                metricName: 'Median',
                colorGetterBuilder: (context) => this.splitColorGetter(context),
                yAxisLabel: {
                    formatter: (v: any) =>
                        EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
                },
                tooltip: {
                    xLabelFormatterGetter: (context: IQueryContext) => {
                        const dayLabel =
                            context.settings?.[
                                QueryContextSettingID.META_DAY_MODE
                            ] === 'install_day'
                                ? 'Install day'
                                : 'Active day';
                        return (label: any) => `${dayLabel} ${label}`;
                    },
                    valueFormatterGetter: () =>
                        EChartsTooltipHelper.tooltipPercentValueFormatter,
                    secondaryMetricsGetter: () => [
                        {
                            name: 'Sample (n)',
                            key: 'hopa_time_share_by_session_n',
                            formatter:
                                EChartsTooltipHelper.tooltipIntegerValueFormatter,
                        },
                    ],
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
        };

    // UBI-1270: Avg counterpart of the event-context HOPA Daily Time Share above.
    avg_hopa_time_share_by_session_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.avg_hopa_time_share_by_session_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.avg_hopa_time_share_by_session_by_day_no',
            dashboardItem: {
                title: (context: IQueryContext) =>
                    context.settings?.[QueryContextSettingID.META_DAY_MODE] ===
                    'install_day'
                        ? 'Avg HOPA Daily Time Share by session (%) per active user by install day'
                        : 'Avg HOPA Daily Time Share by session (%) per active user by active day',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.avg_hopa_time_share_by_session_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            echartsSeriesType: 'line',
            yColumnLabel: 'avg_hopa_time_share_by_session',
            metricName: 'Avg',
            colorGetterBuilder: (context) => this.splitColorGetter(context),
            yAxisLabel: {
                formatter: (v: any) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    const dayLabel =
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Install day'
                            : 'Active day';
                    return (label: any) => `${dayLabel} ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
                secondaryMetricsGetter: () => [
                    {
                        name: 'Sample (n)',
                        key: 'hopa_time_share_by_session_n',
                        formatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    },
                ],
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
    };

    // UBI-1270: Shop Daily Time Share — the third slice of the day next to META and HOPA (the three add up to
    // 100% of the session by construction).
    median_shop_time_share_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.median_shop_time_share_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.median_shop_time_share_by_day_no',
            dashboardItem: {
                title: (context: IQueryContext) =>
                    context.settings?.[QueryContextSettingID.META_DAY_MODE] ===
                    'install_day'
                        ? 'Median Shop Daily Time Share (%) per active user by install day'
                        : 'Median Shop Daily Time Share (%) per active user by active day',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.median_shop_time_share_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            echartsSeriesType: 'line',
            yColumnLabel: 'median_shop_time_share',
            metricName: 'Median',
            colorGetterBuilder: (context) => this.splitColorGetter(context),
            yAxisLabel: {
                formatter: (v: any) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    const dayLabel =
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Install day'
                            : 'Active day';
                    return (label: any) => `${dayLabel} ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
                secondaryMetricsGetter: () => [
                    {
                        name: 'Sample (n)',
                        key: 'shop_time_share_n',
                        formatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    },
                ],
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
    };

    // UBI-1270: Avg counterpart of the Shop Daily Time Share chart above.
    avg_shop_time_share_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.avg_shop_time_share_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.avg_shop_time_share_by_day_no',
            dashboardItem: {
                title: (context: IQueryContext) =>
                    context.settings?.[QueryContextSettingID.META_DAY_MODE] ===
                    'install_day'
                        ? 'Avg Shop Daily Time Share (%) per active user by install day'
                        : 'Avg Shop Daily Time Share (%) per active user by active day',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.avg_shop_time_share_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            echartsSeriesType: 'line',
            yColumnLabel: 'avg_shop_time_share',
            metricName: 'Avg',
            colorGetterBuilder: (context) => this.splitColorGetter(context),
            yAxisLabel: {
                formatter: (v: any) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    const dayLabel =
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Install day'
                            : 'Active day';
                    return (label: any) => `${dayLabel} ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
                secondaryMetricsGetter: () => [
                    {
                        name: 'Sample (n)',
                        key: 'shop_time_share_n',
                        formatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    },
                ],
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
    };

    // PROTOTYPE: New Decos Placed by active users histogram
    new_decos_placed_by_active_users_histogram: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.new_decos_placed_by_active_users_histogram',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.new_decos_placed_by_active_users_histogram',
            dashboardItem: {
                title: (context: IQueryContext) => {
                    const days: number[] = (context.settings?.[
                        QueryContextSettingID
                            .ENGAGEMENT_SELECTED_DAY_AND_SESSION
                    ] as number[] | undefined) ?? [1];
                    return `New Decos Placed by active users histogram - d${days.join(', d')}`;
                },
                subtitle: 'Selected day multiselect',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.new_decos_placed_by_active_users_histogram,
            },
            xColumnLabel: 'deco_count',
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            echartsSeriesType: 'bar',
            metricsGetter: (context: IQueryContext) => {
                const days: number[] = (context.settings?.[
                    QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION
                ] as number[] | undefined) ?? [1];
                const metrics: Record<
                    string,
                    { echartsSeriesOption: {}; name: string }
                > = {};
                days.forEach((d) => {
                    metrics[`d${d}`] = {
                        echartsSeriesOption: {},
                        name: `d${d}`,
                    };
                });
                return metrics;
            },
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter: () => (label: any) => `${label} decos`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.decos_histogram']
                .emitter,
    };

    // PROTOTYPE: Median Daily Inspiration Gain per active user by install day / active day
    median_daily_inspiration_gain_per_active_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.meta-engagement.chart.median_daily_inspiration_gain_per_active_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.meta-engagement.chart.median_daily_inspiration_gain_per_active_user_by_day_no',
                dashboardItem: {
                    title: (context: IQueryContext) =>
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Median Daily Inspiration Gain per active user by install day'
                            : 'Median Daily Inspiration Gain per active user by active day',
                    chartDocumentation:
                        MopaMetaEngagementChartDocs.median_daily_inspiration_gain_per_active_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                echartsSeriesType: 'line',
                yColumnLabel: 'median_daily_inspiration_gain',
                metricName: 'Median',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter: (context: IQueryContext) => {
                        const dayLabel =
                            context.settings?.[
                                QueryContextSettingID.META_DAY_MODE
                            ] === 'install_day'
                                ? 'Install day'
                                : 'Active day';
                        return (label: any) => `${dayLabel} ${label}`;
                    },
                    valueFormatterGetter: () =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter,
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
        };

    // Avg Daily Inspiration Gain per active user by install day / active day (UBI-1185)
    avg_daily_inspiration_gain_per_active_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.meta-engagement.chart.avg_daily_inspiration_gain_per_active_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.meta-engagement.chart.avg_daily_inspiration_gain_per_active_user_by_day_no',
                dashboardItem: {
                    title: (context: IQueryContext) =>
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Avg Daily Inspiration Gain per active user by install day'
                            : 'Avg Daily Inspiration Gain per active user by active day',
                    chartDocumentation:
                        MopaMetaEngagementChartDocs.avg_daily_inspiration_gain_per_active_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                echartsSeriesType: 'line',
                yColumnLabel: 'avg_daily_inspiration_gain',
                metricName: 'Avg',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter: (context: IQueryContext) => {
                        const dayLabel =
                            context.settings?.[
                                QueryContextSettingID.META_DAY_MODE
                            ] === 'install_day'
                                ? 'Install day'
                                : 'Active day';
                        return (label: any) => `${dayLabel} ${label}`;
                    },
                    valueFormatterGetter: () =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter,
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
        };

    // Median Career Level Reached per active user by install day / active day
    median_career_level_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.median_career_level_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.median_career_level_by_day_no',
            dashboardItem: {
                title: (context: IQueryContext) =>
                    context.settings?.[QueryContextSettingID.META_DAY_MODE] ===
                    'install_day'
                        ? 'Median Career Level Reached per active user by install day'
                        : 'Median Career Level Reached per active user by active day',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.median_career_level_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            echartsSeriesType: 'line',
            yColumnLabel: 'median_career_level',
            metricName: 'Median',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    const dayLabel =
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Install day'
                            : 'Active day';
                    return (label: any) => `${dayLabel} ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipNumberValueFormatter,
                secondaryMetricsGetter: () => [
                    {
                        name: 'Sample (n)',
                        key: 'career_level_n',
                        formatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    },
                ],
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
    };

    // Avg Career Level Reached per active user by install day / active day
    avg_career_level_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-engagement.chart.avg_career_level_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.meta-engagement.chart.avg_career_level_by_day_no',
            dashboardItem: {
                title: (context: IQueryContext) =>
                    context.settings?.[QueryContextSettingID.META_DAY_MODE] ===
                    'install_day'
                        ? 'Avg Career Level Reached per active user by install day'
                        : 'Avg Career Level Reached per active user by active day',
                chartDocumentation:
                    MopaMetaEngagementChartDocs.avg_career_level_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            echartsSeriesType: 'line',
            yColumnLabel: 'avg_career_level',
            metricName: 'Avg',
            colorGetterBuilder: (context: IQueryContext) => {
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    const dayLabel =
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Install day'
                            : 'Active day';
                    return (label: any) => `${dayLabel} ${label}`;
                },
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipNumberValueFormatter,
                secondaryMetricsGetter: () => [
                    {
                        name: 'Sample (n)',
                        key: 'career_level_n',
                        formatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    },
                ],
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-engagement.by_day_no'].emitter,
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        ACTIVITY_DATE: {
            id: 'mopa.meta-engagement.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.meta-engagement.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                splitable: false,
            }),
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        ACTIVITY_APP_VERSION_GROUP_MINOR: {
            id: 'mopa.meta-engagement.filter.app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.app_version_group_minor',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.activity_app_version_group_minor'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'mopa.meta-engagement.filter.app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.app_version',
                    'ACTIVITY_APP_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.activity_app_version'
                ].emitter,
        },
        DAILY_TOTAL_ENERGY_SPENT: {
            id: 'mopa.meta-engagement.filter.daily_total_energy_spent',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.meta-engagement.filter.daily_total_energy_spent',
                datasourceColumnId: 'DAILY_TOTAL_ENERGY_SPENT',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.daily_total_energy_spent'
                ].emitter,
        },
        SEGMENT: {
            id: 'mopa.meta-engagement.filter.segments',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'mopa.meta-engagement.filter.segments',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.meta-engagement.filter.segment']
                    .emitter,
        },
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'mopa.meta-engagement.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.meta-engagement.filter.firebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                splitable: true,
            }),
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.meta-engagement.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.meta-engagement.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.firebase_country_european_union',
                    'FIREBASE_COUNTRY_EUROPEAN_UNION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.meta-engagement.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.meta-engagement.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.firebase_country'
                ].emitter,
        },
        FIREBASE_COUNTRY_US_REGION: {
            id: 'mopa.meta-engagement.filter.firebase_country_us_region',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.firebase_country_us_region',
                    'FIREBASE_COUNTRY_US_REGION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.firebase_country_us_region'
                ].emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'mopa.meta-engagement.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.attribution_traffic_source',
                    'ATTRIBUTION_TRAFFIC_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.attribution_traffic_source'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
            id: 'mopa.meta-engagement.filter.firebase_install_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.firebase_install_version_group_minor',
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.firebase_install_version_group_minor'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION: {
            id: 'mopa.meta-engagement.filter.firebase_install_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.firebase_install_version',
                    'FIREBASE_INSTALL_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.firebase_install_version'
                ].emitter,
        },
        ETL_PAYER: {
            id: 'mopa.meta-engagement.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.etl_payer',
                    'ETL_PAYER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.meta-engagement.filter.etl_payer']
                    .emitter,
        },
        ETL_REVENUE_BADGE: {
            id: 'mopa.meta-engagement.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.etl_revenue_badge',
                    'ETL_REVENUE_BADGE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.etl_revenue_badge'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'mopa.meta-engagement.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.attribution_network_name',
                    'ATTRIBUTION_NETWORK_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.attribution_network_name'
                ].emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'mopa.meta-engagement.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-engagement.filter.attribution_campaign_name',
                    'ATTRIBUTION_CAMPAIGN_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-engagement.filter.attribution_campaign_name'
                ].emitter,
        },
    };

    private userTypeMetrics(
        context: IQueryContext,
        keys: {
            all: string;
            meta: string;
            hopa: string;
            coreLoop: string;
            metaProd: string;
        }
    ) {
        const showAll =
            !!context.settings?.[QueryContextSettingID.META_SHOW_ALL_USERS];
        const showMeta =
            !!context.settings?.[QueryContextSettingID.META_SHOW_META_USERS];
        const showHopa =
            !!context.settings?.[QueryContextSettingID.META_SHOW_HOPA_USERS];
        const showMetaProd =
            !!context.settings?.[
                QueryContextSettingID.META_SHOW_META_PRODUCTIVE_USERS
            ];
        const showCoreLoop =
            !!context.settings?.[
                QueryContextSettingID.META_SHOW_CORE_LOOP_USERS
            ];
        return {
            ...(showAll && {
                [keys.all]: {
                    name: 'All Users',
                    echartsSeriesOption: {
                        lineStyle: { type: 'line', width: 1 },
                    },
                },
            }),
            ...(showMeta && {
                [keys.meta]: {
                    name: 'META Users',
                    echartsSeriesOption: {
                        lineStyle: { type: 'dotted', width: 1 },
                    },
                },
            }),
            ...(showHopa && {
                [keys.hopa]: {
                    name: 'HOPA Users',
                    echartsSeriesOption: {
                        lineStyle: { type: 'dashed', width: 1 },
                    },
                },
            }),
            ...(showCoreLoop && {
                [keys.coreLoop]: {
                    name: 'CORE LOOP Users',
                    echartsSeriesOption: {
                        lineStyle: { type: [2, 5, 2, 5], width: 1 },
                    },
                },
            }),
            ...(showMetaProd && {
                [keys.metaProd]: {
                    name: 'META Prod. Users',
                    echartsSeriesOption: {
                        lineStyle: { type: [4, 1, 4, 1], width: 1 },
                    },
                },
            }),
        };
    }

    private splitColorGetter(context: IQueryContext) {
        const splitColumnsIds = context.datasourceColumns
            .filter((dc) => dc.state.isSplitApplied)
            .map((dc) => dc.id);
        const layer = ChartColorService.getLayerString(splitColumnsIds);
        return (s: string) => ({ color: this.colorService.getColor(layer, s) });
    }

    override async ngOnInit() {
        super.ngOnInit();
    }

    async ngAfterViewInit() {
        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 365 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        initialContext.datasourceColumns = [activityDateIC];

        initialContext.settings[
            QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION
        ] = [1];
        initialContext.settings[QueryContextSettingID.RETENTION_MAX_DAY_NO] =
            '180';
        initialContext.settings[QueryContextSettingID.META_DAY_MODE] =
            'install_day';
        initialContext.settings[QueryContextSettingID.META_SHOW_META_USERS] =
            true;
        initialContext.settings[QueryContextSettingID.META_SHOW_HOPA_USERS] =
            true;
        initialContext.settings[
            QueryContextSettingID.META_SHOW_META_PRODUCTIVE_USERS
        ] = true;
        initialContext.settings[
            QueryContextSettingID.META_SHOW_CORE_LOOP_USERS
        ] = true;

        return initialContext;
    }
}
